import { useEffect, useMemo, useRef, useState } from "react";
import { useFormState, useWatch } from "react-hook-form";
import debounce from "lodash/debounce";
import cloneDeep from "lodash/cloneDeep";

export type ExcludeFieldsOption = string[] | ((field: string) => boolean | string[]);

// Campos que serán excluidos del borrador
const EXCLUDED_FIELDS: string[] = [];

interface UseAutoLoadErrorsOptions {
  // Función para guardar el borrador
  onLoadErrors: (errors: any) => Promise<void>;
  // Función opcional para limpiar el borrador
  onCancel?: () => Promise<void>;
  // Tiempo de espera entre guardados (ms)
  debounceTime?: number;
  // Campos adicionales a excluir
  excludeFields?: ExcludeFieldsOption;
  // Modo de edición o creación
  schema: any;
  pickFields?: string[];

  fnTransformLabels?: (label: string) => string;
  isEdit?: boolean;
}

export const useAutoLoadErrors = (form: any, options: UseAutoLoadErrorsOptions) => {
  const {
    onLoadErrors,
    debounceTime = 1000,
    excludeFields,
    pickFields = [],
    schema,
    fnTransformLabels,
    isEdit = false
  } = options;

  const formState = useFormState({ control: form.control });
  const watchedData = useWatch({ control: form.control });

  const previousValues = useRef<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<any>({});

  const cleanExcludedFields = (data: any) => {
    const cleanData = cloneDeep(data);

    const removeExcludedFields = (obj: any) => {
      if (!obj || typeof obj !== "object") {
        return;
      }

      for (const key in obj) {
        if (matchesExclude(key, excludeFields)) {
          delete obj[key];
          continue;
        }

        if (obj[key] && typeof obj[key] === "object") {
          removeExcludedFields(obj[key]);
        }
      }
    };

    removeExcludedFields(cleanData);
    return cleanData;
  };

  // Función para guardar el borrador
  const loadErrors = debounce(async (data: any) => {
    const cleanData = cleanExcludedFields(data);

    if (
      previousValues.current &&
      JSON.stringify(cleanData) === JSON.stringify(previousValues.current)
    ) {
      return;
    }

    try {
      setIsLoading(true);
      await schema.validate(cleanData, {
        abortEarly: false,
        strict: false,
        context: watchedData // Pass the form state as context for conditional validation
      });
      setErrors({});
      onLoadErrors({});
    } catch (error: any) {
      const newErrors: Record<string, string> = {};
      if (error?.inner && Array.isArray(error?.inner)) {
        const relevantErrors = filterErrors(
          error.inner,
          pickFields || [],
          excludeFields
        );
        relevantErrors.forEach((error: any) => {
          // Use label if available, otherwise path
          if (error.path.includes("[")) {
            if (fnTransformLabels) {
              const parsedName = fnTransformLabels(error.path);
              newErrors[parsedName] = error.message;
            } else {
              newErrors[error.path] = error.message;
            }
          } else if (error.params?.label) {
            newErrors[error.params.label] = error.message;
          } else {
            newErrors[error.path] = error.message;
          }
        });
      } else {
        // Handle unexpected error formats
        newErrors["general"] = "Error de validación general";
      }
      const sanitizedErrors = Object.entries(newErrors).reduce<
        Record<string, string>
      >((acc, [key, value]) => {
        if (!matchesExclude(key, excludeFields)) {
          acc[key] = value;
        }
        return acc;
      }, {});

      setErrors({ ...sanitizedErrors });
      onLoadErrors({ ...sanitizedErrors });
    } finally {
      previousValues.current = cleanData;
      setIsLoading(false);
    }
  }, debounceTime);

  const cancelDraft = async () => {
    try {
      previousValues.current = null;
      loadErrors.cancel();
    } catch (error) {
      console.error("Error canceling :", error);
    }
  };

  useEffect(() => {
    if (isEdit) {
      loadErrors({ ...watchedData });
      return;
    }

    if (formState.isDirty) {
      loadErrors({ ...watchedData });
    }
    return () => loadErrors.cancel();
  }, [watchedData]);

  const getStatus = () => {
    if (isLoading) {
      return "loading";
    }

    if (Object.keys(errors).length > 0) {
      return "error";
    }

    if (Object.keys(errors).length === 0 && !isLoading && formState.isDirty) {
      return "success";
    }

    if (
      Object.keys(errors).length === 0 &&
      !isLoading &&
      !formState.isDirty &&
      isEdit
    ) {
      return "success";
    }

    return "info";
  };

  const status = useMemo(() => {
    return {
      isLoading,
      isError: Object.keys(errors).length > 0,
      isSuccess: Object.keys(errors).length === 0,
      isInfo: Object.keys(errors).length === 0 && !isLoading && !formState.isDirty,
      statusString: getStatus()
    };
  }, [errors, isLoading, formState.isDirty]);

  return {
    isLoading,
    cancelDraft,
    status
  };
};

const filterErrors = (
  errors: any[],
  fieldNames: string[],
  excludeFields?: ExcludeFieldsOption
) => {
  // Normalize field names for efficient checks
  const exactPaths = new Set<string>(); // e.g. "foo[0].bar"
  const baseArrayPaths = new Set<string>(); // e.g. "building" (matches any building[...].*)
  const arraySubfields: Array<{ base: string; sub: string }> = []; // e.g. { base: 'building', sub: 'name' }

  for (const f of fieldNames) {
    if (!f) continue;
    if (f.includes("[")) {
      exactPaths.add(f);
      continue;
    }

    // Support pattern like "building.name" to match building[<i>].name
    if (f.includes(".")) {
      const [base, ...rest] = f.split(".");
      const sub = rest.join(".");
      if (base && sub) {
        arraySubfields.push({ base, sub });
        continue;
      }
    }

    // Plain field or base array path
    baseArrayPaths.add(f);
  }

  const isFirstLevelArraySubfield = (path: string, base: string, sub: string) => {
    // Matches base[<index>].sub exactly (no deeper nesting)
    // Example: building[0].name
    const re = new RegExp(`^${base}\\[\\d+\\]\\.${sub}$`);
    return re.test(path);
  };

  return errors.filter(err => {
    const p: string = err.path || "";
    if (!p) return false;

    if (shouldExcludeError(err, excludeFields)) return false;

    // 1) Exact path match
    if (exactPaths.has(p)) return true;

    // 2) Base array path match (legacy behavior: any error under base)
    if (p.includes("[")) {
      const base = p.split("[")[0];
      if (baseArrayPaths.has(base)) return true;

      // 3) Array subfield match like building[0].name for requested "building.name"
      for (const { base: b, sub } of arraySubfields) {
        if (base === b && isFirstLevelArraySubfield(p, b, sub)) return true;
      }
    } else {
      // Non-array flat path
      if (baseArrayPaths.has(p)) return true;
    }

    return false;
  });
};

function shouldExcludeError(error: any, excludeFields?: ExcludeFieldsOption) {
  if (!excludeFields) return false;

  const candidates = collectErrorCandidates(error);
  return candidates.some(candidate => matchesExclude(candidate, excludeFields));
}

function collectErrorCandidates(error: any): string[] {
  const candidates = new Set<string>();

  const path = typeof error?.path === "string" ? error.path : "";
  if (path) {
    candidates.add(path);
    candidates.add(path.replace(/\[\d+]/g, "[]"));
    const pathWithoutIndexes = path.replace(/\[\d+]/g, "");
    pathWithoutIndexes
      .split(".")
      .map(segment => segment.trim())
      .filter(Boolean)
      .forEach(segment => candidates.add(segment));
  }

  const label =
    typeof error?.params?.label === "string" ? error.params.label : undefined;
  if (label) {
    candidates.add(label);
  }

  return Array.from(candidates.values());
}

function matchesExclude(
  value: string | undefined,
  excludeFields?: ExcludeFieldsOption
) {
  if (!value || !excludeFields) {
    return false;
  }

  if (Array.isArray(excludeFields)) {
    return excludeFields.includes(value);
  }

  const result = excludeFields(value);

  if (typeof result === "boolean") {
    return result;
  }

  if (Array.isArray(result)) {
    return result.includes(value);
  }

  return false;
}
